import dayjs from 'dayjs';
import { reactive } from 'vue';

import constants, {
  CONTENT_TAG_MATCH_TYPES,
  MESSAGE_TAG_MATCH_TYPES,
} from '@/app/dashboards/constants';
import { GRAPH_SCALES, isValidGraphScale } from '@/models/dashboards/graph-scales.enum';
import { browserStorageGetItem, browserStorageSetItem } from '@/utils/browserStorage';
import { formatInTimeZone } from '@/utils/datetimeUtils';
import { endOfDay, formatDate, startOfDay, subDays } from '@/utils/dateUtils';

export function useDashboardsReportFilters() {
  const dashboardsFilters = reactive({
    reportDateRange: [],
    contextDateRange: [],
    reportingPeriodMode: null,
    contextPeriodMode: null,
    graphScale: null,
    contentTagIds: [],
    contentTagMatchType: CONTENT_TAG_MATCH_TYPES.ANY,
    messageTagIds: [],
    messageTagMatchType: MESSAGE_TAG_MATCH_TYPES.ANY,
    brandIds: [],
    brandTagIds: [],
    competitorSourceAccountIds: [],
    competitorTagIds: [],
    campaignIds: [],
    boardIds: [],
  });

  function isValidDateRange(dateRange) {
    return (
      Array.isArray(dateRange) &&
      dateRange.length === 2 &&
      dateRange.every((date) => dayjs(date, 'YYYY-MM-DD', true).isValid())
    );
  }

  function isValidMode(value, mode) {
    return value && value in mode;
  }

  function generateDashboardLocalStorageKey(key, dashboardId, currentDashboard) {
    const dashboardKey = `-${dashboardId || currentDashboard?.id}`;
    return `${key}${dashboardKey}`;
  }

  function generateDefaultReportDateRange(timeZone) {
    const Yesterday = subDays(new Date(), 1);

    const end = endOfDay(Yesterday);
    const start = subDays(startOfDay(end), 6);

    const fmt = constants.FORMAT_STRING;
    const format = (date) =>
      timeZone ? formatInTimeZone(date, timeZone, fmt) : formatDate(date, fmt);
    return [format(start), format(end)];
  }

  function generateDefaultContextDateRange(timeZone) {
    const previousWeek = subDays(new Date(), 8);

    const end = endOfDay(previousWeek);
    const start = subDays(startOfDay(end), 6);

    const fmt = constants.FORMAT_STRING;
    const format = (date) =>
      timeZone ? formatInTimeZone(date, timeZone, fmt) : formatDate(date, fmt);
    return [format(start), format(end)];
  }

  function updateReportDate({ newReportDateRange, dashboardId, currentDashboard, timeZone }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.REPORT_DATE_RANGE_LS_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageDateRange =
      browserStorageGetItem(localStorageKey)?.toString()?.split(',') || null;
    let dateToBeUsed = null;

    if (isValidDateRange(newReportDateRange)) {
      dateToBeUsed = newReportDateRange;
    } else if (isValidDateRange(localStorageDateRange)) {
      dateToBeUsed = localStorageDateRange;
    } else {
      dateToBeUsed = generateDefaultReportDateRange(timeZone);
    }

    dashboardsFilters.reportDateRange = dateToBeUsed;
    browserStorageSetItem(localStorageKey, dateToBeUsed.join(','));
  }

  function updateContextDate({ newContextDateRange, dashboardId, currentDashboard, timeZone }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.CONTEXT_DATE_RANGE_LS_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageDateRange =
      browserStorageGetItem(localStorageKey)?.toString()?.split(',') || null;
    let dateToBeUsed = null;
    if (isValidDateRange(newContextDateRange)) {
      dateToBeUsed = newContextDateRange;
    } else if (isValidDateRange(localStorageDateRange)) {
      dateToBeUsed = localStorageDateRange;
    } else {
      dateToBeUsed = generateDefaultContextDateRange(timeZone);
    }

    dashboardsFilters.contextDateRange = dateToBeUsed;
    browserStorageSetItem(localStorageKey, dateToBeUsed.join(','));
  }

  function updateReportingPeriodMode({ mode, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.REPORTING_PERIOD_MODE_LS_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageValue = browserStorageGetItem(localStorageKey)?.toString() || null;
    let valueToBeUsed = null;
    if (isValidMode(mode, constants.REPORTING_PERIOD_FILTERS)) {
      valueToBeUsed = mode;
    } else if (isValidMode(localStorageValue, constants.REPORTING_PERIOD_FILTERS)) {
      valueToBeUsed = localStorageValue;
    } else {
      valueToBeUsed = constants.REPORTING_PERIOD_FILTERS.LAST_7_DAYS.value;
    }

    dashboardsFilters.reportingPeriodMode = valueToBeUsed;
    browserStorageSetItem(localStorageKey, valueToBeUsed);
  }

  function updateContextPeriodMode({ mode, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.CONTEXT_PERIOD_MODE_LS_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageValue = browserStorageGetItem(localStorageKey)?.toString() || null;
    let valueToBeUsed = null;
    if (isValidMode(mode, constants.COMPARISON_PERIOD_FILTERS)) {
      valueToBeUsed = mode;
    } else if (isValidMode(localStorageValue, constants.COMPARISON_PERIOD_FILTERS)) {
      valueToBeUsed = localStorageValue;
    } else {
      valueToBeUsed = constants.COMPARISON_PERIOD_FILTERS.PREVIOUS_7_DAYS.value;
    }

    dashboardsFilters.contextPeriodMode = valueToBeUsed;
    browserStorageSetItem(localStorageKey, valueToBeUsed);
  }

  function updateGraphScale({ scale, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.GRAPH_SCALE_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageGraphScale = browserStorageGetItem(localStorageKey)?.toString() || null;
    let graphScaleToBeUsed = null;
    if (isValidGraphScale(scale)) {
      graphScaleToBeUsed = scale;
    } else if (isValidGraphScale(localStorageGraphScale)) {
      graphScaleToBeUsed = localStorageGraphScale;
    } else {
      graphScaleToBeUsed = GRAPH_SCALES.DAILY.value;
    }
    dashboardsFilters.graphScale = graphScaleToBeUsed;
    browserStorageSetItem(localStorageKey, graphScaleToBeUsed);
  }

  function updateContentTagIds({ contentTagIds, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.CONTENT_TAG_IDS_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageContentTagIds = browserStorageGetItem(localStorageKey) || null;
    let useContentTagIds;
    if (contentTagIds) {
      useContentTagIds = contentTagIds;
    } else if (localStorageContentTagIds) {
      useContentTagIds = localStorageContentTagIds
        .split(',')
        .map((contentTagId) => parseInt(contentTagId, 10));
    } else {
      useContentTagIds = [];
    }
    dashboardsFilters.contentTagIds = useContentTagIds;
    browserStorageSetItem(localStorageKey, useContentTagIds);
  }

  function updateContentTagMatchType({ contentTagMatchType, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.CONTENT_TAG_MATCH_TYPE_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );
    const localStorageContentTagMatchType = browserStorageGetItem(localStorageKey) || null;
    let useContentTagMatchType;
    if (contentTagMatchType) {
      useContentTagMatchType = contentTagMatchType;
    } else if (localStorageContentTagMatchType) {
      useContentTagMatchType = localStorageContentTagMatchType;
    } else {
      useContentTagMatchType = CONTENT_TAG_MATCH_TYPES.ANY;
    }
    dashboardsFilters.contentTagMatchType = useContentTagMatchType;
    browserStorageSetItem(localStorageKey, useContentTagMatchType);
  }

  function updateMessageTagIds({ messageTagIds, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.MESSAGE_TAG_IDS_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageMessageTagIds = browserStorageGetItem(localStorageKey) || null;
    let useMessageTagIds;
    if (messageTagIds) {
      useMessageTagIds = messageTagIds;
    } else if (localStorageMessageTagIds) {
      useMessageTagIds = localStorageMessageTagIds
        .split(',')
        .map((messageTagId) => parseInt(messageTagId, 10));
    } else {
      useMessageTagIds = [];
    }
    dashboardsFilters.messageTagIds = useMessageTagIds;
    browserStorageSetItem(localStorageKey, useMessageTagIds);
  }

  function updateMessageTagMatchType({ messageTagMatchType, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.MESSAGE_TAG_MATCH_TYPE_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );
    const localStorageMessageTagMatchType = browserStorageGetItem(localStorageKey) || null;
    let useMessageTagMatchType;
    if (messageTagMatchType) {
      useMessageTagMatchType = messageTagMatchType;
    } else if (localStorageMessageTagMatchType) {
      useMessageTagMatchType = localStorageMessageTagMatchType;
    } else {
      useMessageTagMatchType = MESSAGE_TAG_MATCH_TYPES.ANY;
    }
    dashboardsFilters.messageTagMatchType = useMessageTagMatchType;
    browserStorageSetItem(localStorageKey, useMessageTagMatchType);
  }

  function updateBrandIds({ brandIds, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.BRAND_IDS_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageBrandIds = browserStorageGetItem(localStorageKey) || null;
    let useBrandIds;
    if (brandIds) {
      useBrandIds = brandIds;
    } else if (localStorageBrandIds) {
      useBrandIds = localStorageBrandIds.split(',').map((brandId) => parseInt(brandId, 10));
    } else {
      useBrandIds = [];
    }
    dashboardsFilters.brandIds = useBrandIds;
    browserStorageSetItem(localStorageKey, useBrandIds);
  }

  function updateBrandTagIds({ brandTagIds, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.BRAND_TAG_IDS_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageBrandIds = browserStorageGetItem(localStorageKey) || null;
    let useBrandTagIds;
    if (brandTagIds) {
      useBrandTagIds = brandTagIds;
    } else if (localStorageBrandIds) {
      useBrandTagIds = localStorageBrandIds.split(',').map((brandId) => parseInt(brandId, 10));
    } else {
      useBrandTagIds = [];
    }
    dashboardsFilters.brandTagIds = useBrandTagIds;
    browserStorageSetItem(localStorageKey, useBrandTagIds);
  }

  function updateCompetitorSourceAccountIds({
    competitorSourceAccountIds,
    dashboardId,
    currentDashboard,
  }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.COMPETITOR_IDS_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageCompetitorIds = browserStorageGetItem(localStorageKey) || null;
    let useCompetitorSourceAccountIds;
    if (competitorSourceAccountIds) {
      useCompetitorSourceAccountIds = competitorSourceAccountIds;
    } else if (localStorageCompetitorIds) {
      useCompetitorSourceAccountIds = localStorageCompetitorIds.split(',');
    } else {
      useCompetitorSourceAccountIds = [];
    }
    dashboardsFilters.competitorSourceAccountIds = useCompetitorSourceAccountIds;
    browserStorageSetItem(localStorageKey, useCompetitorSourceAccountIds);
  }

  function updateCompetitorTagIds({ competitorTagIds, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.COMPETITOR_TAG_IDS_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageBrandIds = browserStorageGetItem(localStorageKey) || null;
    let useCompetitorTagIds;
    if (competitorTagIds) {
      useCompetitorTagIds = competitorTagIds;
    } else if (localStorageBrandIds) {
      useCompetitorTagIds = localStorageBrandIds.split(',').map((brandId) => parseInt(brandId, 10));
    } else {
      useCompetitorTagIds = [];
    }
    dashboardsFilters.competitorTagIds = useCompetitorTagIds;
    browserStorageSetItem(localStorageKey, useCompetitorTagIds);
  }

  function updateCampaignIds({ campaignIds, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.CAMPAIGN_IDS_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageCampaignIds = browserStorageGetItem(localStorageKey) || null;
    let useCampaignIds;
    if (campaignIds) {
      useCampaignIds = campaignIds;
    } else if (localStorageCampaignIds) {
      useCampaignIds = localStorageCampaignIds
        .split(',')
        .map((campaignId) => parseInt(campaignId, 10));
    } else {
      useCampaignIds = [];
    }
    dashboardsFilters.campaignIds = useCampaignIds;
    browserStorageSetItem(localStorageKey, useCampaignIds);
  }

  function updateBoardIds({ boardIds, dashboardId, currentDashboard }) {
    const localStorageKey = generateDashboardLocalStorageKey(
      constants.BOARD_IDS_LOCAL_STORE_KEY,
      dashboardId,
      currentDashboard,
    );

    const localStorageBoardIds = browserStorageGetItem(localStorageKey) || null;
    let useBoardIds;
    if (boardIds) {
      useBoardIds = boardIds;
    } else if (localStorageBoardIds) {
      useBoardIds = localStorageBoardIds.split(',').map((boardId) => parseInt(boardId, 10));
    } else {
      useBoardIds = [];
    }
    dashboardsFilters.boardIds = useBoardIds;
    browserStorageSetItem(localStorageKey, useBoardIds);
  }

  return {
    dashboardsFilters,
    updateReportDate,
    updateContextDate,
    updateReportingPeriodMode,
    updateContextPeriodMode,
    updateGraphScale,
    updateContentTagIds,
    updateContentTagMatchType,
    updateMessageTagIds,
    updateMessageTagMatchType,
    updateBrandIds,
    updateBrandTagIds,
    updateCompetitorSourceAccountIds,
    updateCompetitorTagIds,
    updateCampaignIds,
    updateBoardIds,
  };
}
